import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";
import { useProfile } from "../contexts/ProfileContext";
import { showToast } from "./Toast";
import logger from "../utils/logger";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

// Weekly star delivery per tier (mirrors backend core/pricing star_delivery)
const WEEKLY_STARS = { PLUS: 250, PRO: 250, FAMILY: 650 };

/**
 * "Start membership now" — lets a trialer end the trial early: bills the plan
 * immediately, and the RC NORMAL event delivers their weekly stars minutes
 * later. Renders nothing unless the user is currently trialing.
 *
 * variant="banner": full-width promo card (star shop / low-stars moment)
 * variant="pill":   compact button (Settings subscription card)
 */
const StartMembershipNow = ({ variant = "pill", onConverted }) => {
  const { user, subscriptionStatus, refreshSubscriptionStatus } = useAuth();
  const { apiClient } = useAPI();
  const { requireParentalVerification } = useProfile();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const isTrialing = subscriptionStatus?.isTrialing === true;
  if (!isTrialing || done) return null;

  const weeklyStars = WEEKLY_STARS[subscriptionStatus?.subscriptionType] || 250;

  const convert = () => {
    requireParentalVerification("start Pixley membership", async () => {
      if (busy || !user?.id) return;
      setBusy(true);
      try {
        const resp = await apiClient.convertTrialNow(user.id);
        if (resp?.status === "success" || resp?.status === true) {
          setDone(true);
          showToast(
            `Membership started! Your ${weeklyStars} weekly stars are on the way.`,
            "success",
            6000,
          );
          // Stars + trial state flow through Stripe -> RevenueCat webhooks;
          // give them a beat, then force-refresh past the caches (twice, in
          // case the first lands before RC ingests).
          setTimeout(() => refreshSubscriptionStatus?.(), 4000);
          setTimeout(() => refreshSubscriptionStatus?.(), 15000);
          onConverted?.();
        } else {
          showToast("Couldn't start your membership. Please try again.", "error");
        }
      } catch (e) {
        logger.error("convert-trial-now failed", e);
        const noTrial = e?.status === 404 || `${e?.message}`.includes("404");
        showToast(
          noTrial
            ? "No active trial found on your account."
            : "Couldn't start your membership. Please try again.",
          "error",
        );
      } finally {
        setBusy(false);
      }
    });
  };

  if (variant === "banner") {
    return (
      <div
        className="w-full flex flex-col sm:flex-row items-center gap-3"
        style={{
          background: "linear-gradient(120deg, #EAF3FF 0%, #F5FAFF 100%)",
          border: "2px solid rgba(0,101,248,0.18)",
          borderRadius: 18,
          padding: "14px 16px",
          marginBottom: 16,
        }}
      >
        <div className="flex-1 text-center sm:text-left">
          <div style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 15 }}>
            Don't wait for your trial to end
          </div>
          <div style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, marginTop: 2 }}>
            Start your membership today and get your {weeklyStars} weekly stars
            instantly. You'll be billed now and your renewal date resets.
          </div>
        </div>
        <button
          type="button"
          onClick={convert}
          disabled={busy}
          className="transition active:scale-[0.97] disabled:opacity-60"
          style={{
            ...FREDOKA,
            background: "#0065F8",
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: 14,
            border: "none",
            borderRadius: 999,
            padding: "11px 20px",
            boxShadow: "0px 4px 0px #073B8E, 0px 10px 20px rgba(0,101,248,0.22)",
            cursor: busy ? "wait" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {busy ? "Starting…" : `Get ${weeklyStars} stars now`}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={convert}
      disabled={busy}
      className="transition active:scale-[0.97] disabled:opacity-60"
      title="End your trial early — you'll be billed today and your weekly stars arrive instantly"
      style={{
        ...FREDOKA,
        background: "#FFFFFF",
        color: "#0065F8",
        fontWeight: 700,
        fontSize: 14,
        border: "2px solid rgba(0,101,248,0.35)",
        borderRadius: 999,
        padding: "10px 18px",
        boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
        cursor: busy ? "wait" : "pointer",
      }}
    >
      {busy ? "Starting…" : "Start membership now"}
    </button>
  );
};

export default StartMembershipNow;
